
import type { FinancialHealthScore } from './types';

/**
 * AI-powered financial health scoring
 */
export function aiFinancialHealthScore(
  balanceSheetData: any[],
  assumptions: any
): FinancialHealthScore {
  const components = [];
  
  // Liquidity Score (25% weight)
  const totalAssets = balanceSheetData
    .filter(entry => entry.account_category === 'Assets')
    .reduce((sum, entry) => sum + Number(entry.balance), 0);
  
  const totalLiabilities = balanceSheetData
    .filter(entry => entry.account_category === 'Liabilities')
    .reduce((sum, entry) => sum + Number(entry.balance), 0);
  
  const liquidityRatio = totalAssets / (totalLiabilities || 1);
  const liquidityScore = Math.min(100, liquidityRatio * 50);
  components.push({ metric: 'Liquidity', score: liquidityScore, weight: 0.25 });
  
  // Profitability Score (30% weight)
  const revenueGrowth = assumptions.profitLoss?.revenueGrowthRate || 0;
  const profitabilityScore = Math.min(100, Math.max(0, revenueGrowth * 5));
  components.push({ metric: 'Profitability', score: profitabilityScore, weight: 0.30 });
  
  // Efficiency Score (25% weight)
  const inventoryTurnover = assumptions.balanceSheet?.inventoryTurnover || 1;
  const efficiencyScore = Math.min(100, inventoryTurnover * 15);
  components.push({ metric: 'Efficiency', score: efficiencyScore, weight: 0.25 });
  
  // Stability Score (20% weight)
  const debtGrowthRate = assumptions.balanceSheet?.debtGrowthRate || 0;
  const stabilityScore = Math.max(0, 100 - debtGrowthRate * 10);
  components.push({ metric: 'Stability', score: stabilityScore, weight: 0.20 });
  
  // Calculate weighted score
  const score = components.reduce((sum, comp) => sum + comp.score * comp.weight, 0);
  
  // Generate insights
  const insights: string[] = [];
  if (liquidityScore < 60) insights.push('Consider improving liquidity ratios');
  if (profitabilityScore < 50) insights.push('Focus on revenue growth initiatives');
  if (efficiencyScore < 40) insights.push('Optimize inventory management');
  if (stabilityScore < 70) insights.push('Monitor debt levels carefully');
  
  // Industry benchmark comparison (simplified)
  const benchmarkComparison = {
    industry: 'Technology',
    percentile: Math.round(score),
    comparison: score > 70 ? 'Above Average' : score > 40 ? 'Average' : 'Below Average'
  };
  
  return {
    score: Math.round(score),
    components,
    insights,
    benchmarkComparison
  };
}
